import { createContext, useContext } from 'react';
import type { AutosaveState } from '@/app/audience-builder/types/draft';

/**
 * Scoped, tree-bound state for the audience-builder wizard. Context — not a
 * Zustand store — because: (1) the state is only meaningful inside the wizard
 * subtree, (2) consumers must see the current value on first render (a store
 * with module-level state flashes stale data on re-entry), and (3) rendering
 * a consumer outside the provider should be a runtime error, not silently
 * return wrong data. See src/CLAUDE.md for the scoped-state Context exception.
 */
export interface WizardDraftContextValue {
  autosaveState: AutosaveState;
  isBooting: boolean;
  /** Saves pending edits before a submit; throws (with a user message) if it cannot (B14). */
  flushBeforeSubmit: () => Promise<void>;
  /** Drops queued edits once the draft is submitted, so nothing saves after it. */
  discardPendingSave: () => void;
}

const WizardDraftContext = createContext<WizardDraftContextValue | null>(null);

export const WizardDraftProvider = WizardDraftContext.Provider;

export const useWizardDraft = (): WizardDraftContextValue => {
  const ctx = useContext(WizardDraftContext);
  if (!ctx) {
    throw new Error('useWizardDraft must be used inside <WizardLayout>');
  }
  return ctx;
};
